<template class="operators-status">
  <div>
    <v-dialog
      v-model="dialog"
      width="70vw"
      height="80vh"
    >
      <v-card>
        <v-card-actions class="operators-status__close-button">
          <DsButton
            v-tooltip="closeLabel"
            color="tertiary"
            name="close"
            :icon="true"
            @click="dialog = false"
          />
        </v-card-actions>
        <v-card-text class="operators-status__modal-style">
          <h3>{{ this.$t('components.StatusTable.faq[0].title') }}</h3>
          <p>{{ this.$t('components.StatusTable.faq[0].description') }}</p>

          <h3>{{ this.$t('components.StatusTable.faq[1].title') }}</h3>
          <p>{{ this.$t('components.StatusTable.faq[1].description') }}</p>

          <h3>{{ this.$t('components.StatusTable.faq[2].title') }}</h3>
          <p>{{ this.$t('components.StatusTable.faq[2].description') }}</p>

          <h3>{{ this.$t('components.StatusTable.faq[3].title') }}</h3>
          <p>{{ this.$t('components.StatusTable.faq[3].description') }}</p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <template v-for="(modal, index) in cardOptionsModals">
      <DynamicModalComponent
        v-if="modal.props.value"
        :key="index"
        :component-name="modal.componentName"
        :custom-props="modal.props"
        :events="modal.events"
      />
    </template>

    <GenericChart
      :request-status="requestStatus"
      :title="title"
      :items="items"
      :map-keys="mapKeys"
      :open-dialog="openDialog"
      :open-filter="openGenericFilterModal"
      :item-id="itemId"
      :has-custom-filter="true"
      v-bind="$attrs"
      class="make-a-copy"
      @reload="getData"
    />
    <v-data-table
      :headers="headers"
      :items="items"
      :loading="inputLoading"
      :options="options"
      :footer-props="footerProps"
      class="operators-status__table-truncate"
    >
      <template v-slot:item.userId="{ item }">
        <DsButton
          v-tooltip="openChatLabel"
          class="ml-9"
          color="ghost"
          name="chat"
          :icon="true"
          @click="openChat({userId: item.userId})"
        />
      </template>
      <template v-slot:item.username="{ item }">
        <div v-tooltip="item.username">
          {{ item.username }}
        </div>
      </template>
      <template
        v-slot:item.status="{ item }"
        class="status-view"
      >
        <StIcon
          icon-name="circle"
          :color="variationIconColor(item.status)"
        />
        {{ translateStatus(item.status) }}
      </template>
      <template v-slot:item.lastStatusChange="{ item }">
        <div v-tooltip="item.lastStatusChange">
          {{ formatDate(item.lastStatusChange) }}
        </div>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import GenericChart from '@/components/cards/models/GenericChart';
import { mapActions, mapGetters, mapState } from 'vuex';
import EventBus from '@/utils/event-bus';
import { convertTimeToBrazilianFormatDate } from '@/utils/date';
import { DsButton } from '@ds/components';
import sacTypeModel from '@/mixins/sac-type-model';
import channelModel from '@/mixins/channel-model';
import { getWarroomURL, getSmartCareURL } from '@/utils/url.js';
import WarRoomService from '@/services/WarRoomService';
import colors from '@/utils/colors';
import StIcon from '@stilingue/st-icon';
import repliersModel from '@/mixins/repliers-model';
import cardsModel from '@/mixins/cards-model';
import filtersModel from '@/mixins/filters-model';
import DynamicModalComponent from '@/components/modal-model/DynamicModalComponent';

export default {
  name: 'OperatorsStatus',
  components: {
    GenericChart,
    DsButton,
    StIcon,
    DynamicModalComponent,
  },
  mixins: [
    sacTypeModel,
    channelModel,
    repliersModel,
    cardsModel,
    filtersModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      options: {
        itemsPerPage: 5,
        page: 1,
      },
      footerProps: {
        'items-per-page-options': [5, 10, 15, 20, 30],
      },
      items: [],
      exportedItems: [],
      requestStatus: 'loading',
      dialog: false,
      filterDialog: false,
      selectedOperators: null,
      selectedStatus: null,
      cardModalMethods: {
        openGenericFilterModal: this.openGenericFilterModalModal,
      },
      hasCustomFilterApplied: false,
    };
  },
  computed: {
    headers() {
      return [
        {
          text: this.$t('components.StatusTable.user'),
          value: 'username',
        },
        {
          text: this.$t('components.StatusTable.status'),
          value: 'status',
        },
        {
          text: this.$t('components.StatusTable.lastStatus'),
          value: 'lastStatusChange',
        },
        {
          text: this.$t('components.StatusTable.viewChat'),
          value: 'userId',
          sortable: false,
        },
      ];
    },
    filterLabel() {
      return this.$t('components.StatusTable.filterLabel');
    },
    trashLabel() {
      return this.$t('components.StatusTable.trashLabel');
    },
    openChatLabel() {
      return this.$t('components.StatusTable.openOperatorLabel');
    },
    closeLabel() {
      return this.$t('components.StatusTable.closeLabel');
    },
    operatorsLabel() {
      return this.$t('components.StatusTable.operators');
    },
    statusLabel() {
      return this.$t('components.StatusTable.status');
    },
    mapKeys() {
      return {
        user_name: this.$t('components.StatusTable.username'),
        status: this.$t('components.StatusTable.status'),
        last_status_change: this.$t('components.StatusTable.lastStatus'),
      };
    },
    selectItems() {
      return {
        online: {
          text: this.$t('components.StatusTable.onlineText'),
          value: 'ONLINE',
        },
        offline: {
          text: this.$t('components.StatusTable.offlineText'),
          value: 'OFFLINE',
        },
      };
    },
    ...mapGetters('filter', [
      'formattedDateRange',
    ]),
    ...mapState('filter', [
      'accountId',
      'universeId',
    ]),
    ...mapState('dashboard', [
      'isStaging',
    ]),
    inputLoading() {
      return this.requestStatus === 'loading';
    },
  },
  watch: {
    options: {
      handler() {
        this.getData();
      },
      deep: true,
    },
  },
  mounted() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getSeStudioEndpointService',
    ]),
    variationIconColor(status) {
      const { positiveColor, ui } = colors;

      if (status === this.selectItems.online.value) {
        return positiveColor;
      }
      return ui.steel;
    },
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      this.applyFilter(options);
    },
    getStatus() {
      return this.card.props.filter && this.card.props.filter.sac_replier_status ? this.card.props.filter.sac_replier_status.value.join(',') : null;
    },
    applyFilter(options) {
      this.requestStatus = 'loading';
      this.items = [];
      this.filterDialog = false;
      this.getSeStudioEndpointService({
        itemId: this.itemId,
        servicePath: 'statusTable',
        serviceParams: {
          status: this.getStatus(),
        },
      })
        .then((response) => {
          this.items = response.users;
          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
          this.requestStatus = 'success';
          this.saveCards();
        })
        .catch((e) => {
          console.error(e);
          this.requestStatus = 'error';
        });
      if (this.selectedOperators || this.selectedStatus) {
        this.hasCustomFilterApplied = true;
      }
    },
    translateStatus(status) {
      return status === this.selectItems.online.value
        ? this.$t('components.StatusTable.onlineText')
        : this.$t('components.StatusTable.offlineText');
    },
    openDialog() {
      this.dialog = true;
    },
    async openChat({ userId }) {
      const newCardFilter = {
        sac_replier: {
          value: [
            String(userId),
          ],
        },
        is_sac: true,
      };
      const filterId = await WarRoomService.encodeFilter({
        account_id: this.accountId,
        is_staging: this.isStaging,
        filter: JSON.stringify(newCardFilter),
      });
      const dateRange = this.formattedDateRange;
      const activatePanelURL = getWarroomURL(this.isStaging, this.accountId, this.universeId);
      const smartCareURL = getSmartCareURL(this.isStaging, dateRange, filterId);
      window.open(activatePanelURL, '_blank').location.replace(smartCareURL);
    },
    formatDate(date) {
      if (!date) {
        return;
      }
      return convertTimeToBrazilianFormatDate(date);
    },
  },
};
</script>

<style lang="scss" scoped>
.operators-status {
  &__internal-style {
    width: 100%;
    padding: 1rem;
    display: flex;
  }

  &__buttons {
    display: flex;
  }

  &__button {
    box-shadow: none;
    border-radius: unset;
  }

  &__close-button {
    display: flex;
    flex: end;
    justify-content: flex-end;
  }

  &__close-icon {
    align-items: center;
  }
}

::v-deep {
  .operators-status {
    &__table-truncate {
      td {
        max-width: 10vw;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &__modal-style {
      * {
        color: var(--ds-color-snow) !important;
      }

      h3 {
        font-weight: var(--ds-font-weight-bold);
      }
    }

    &__v-dialog {
      overflow-x: hidden;
    }
  }
}
</style>
